/* eslint-disable react/no-children-prop */
import { FC, ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { useTranslations } from '@altermaind/core/i18n';
import { Asset, Box, Typography, Svg, useThemeValue } from '@altermaind/web/ui';
import { TutorialKeys } from '@/_shared/constants';
import { FeatureFlag } from '@/_shared/constants/featureFlags';
import { Roles } from '@/_shared/constants/roles';
import { convertSizeToNumber } from '@/_shared/helpers';
import {
  useCalculateMainContentDynamicHeight,
  useDialog,
  useFeatureFlags,
  useTutorialCheck,
} from '@/_shared/hooks';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getModeTheme } from '@/_shared/styles/state/selectors';
import { getAspectForAppCall } from '@/aspect';
import {
  getOrganizationConfigCall,
  getOrganizationsCall,
  getUserAuthorizeCall,
} from '@/core/state/api';
import { ClaimsComponent } from '../Claims';
import { FirstTutorial } from '../FirstTutorial';
import { Menu, MenuLight, MenuProvider, useMenuCollapsed } from '../Menu';
import { UserInfo } from '../UserInfo';
import { Header } from './Header';
import { HeadlessBanners } from './HeadlessBanners';
import { LayoutContext } from './LayoutContext';
import { Sidebar } from './Sidebar';
// eslint-disable-next-line @altermaind/no-build-import
import 'react-toastify/dist/ReactToastify.css';

type Props = {
  children: ReactElement;
};

const PageLayoutContent: FC<Props> = ({ children }) => {
  const tShared = useTranslations();
  const [layout, setLayout] = useState({ title: '' });
  const [banners, setBanners] = useState<Record<string, ReactNode>>({});
  const bannersGapXS = convertSizeToNumber(`${useThemeValue('sizes', 'xs')}`) || 8;

  const setBanner = useCallback((key: string, banner: ReactNode | null) => {
    setBanners(prev => {
      if (banner === null) {
        // If the banner is already missing, preserve the previous state reference
        // to avoid triggering an unnecessary re-render.
        if (!(key in prev)) {
          return prev;
        }

        // Remove the banner by creating a shallow copy first, so the previous
        // state object is not mutated directly.
        const next = { ...prev };
        delete next[key];
        return next;
      }

      // Add a new banner or replace an existing one with the same key.
      return { ...prev, [key]: banner };
    });
  }, []);

  const dynamicHeight = useCalculateMainContentDynamicHeight();
  const mode = useSelector(getModeTheme());
  const { openDialog } = useDialog('firstTutorial');
  const { response: responseGetAspectForApp } = getAspectForAppCall.hook();
  const { userMustConfigureOrganization } = getOrganizationConfigCall.hook();
  const { hasBlockingError } = getUserAuthorizeCall.hook();
  const { hasAlreadySeenTutorial } = useTutorialCheck();
  const { isFeatureEnabled } = useFeatureFlags();
  const isFFTutorial = isFeatureEnabled(FeatureFlag.TUTORIAL);
  const isFFMenuLight = isFeatureEnabled(FeatureFlag.MENULIGHT);

  const { genericResponsiveValue } = useResponsiveStyle();
  const { isCollapsed, toggleCollapsed } = useMenuCollapsed();

  const { defaultOrganization } = getOrganizationsCall.hook();

  const logoSlash = useMemo(() => {
    return mode === 'light' ? 'logoLight' : 'logoDark';
  }, [mode]);

  const imageBase64 = useMemo(() => {
    if (responseGetAspectForApp?.logo?.light && mode === 'light') {
      return responseGetAspectForApp.logo.light;
    }
    return responseGetAspectForApp?.logo?.dark;
  }, [mode, responseGetAspectForApp]);

  const { isDesktop } = useBreakingPoints();

  const smallSize = useThemeValue('sizes', 's');

  const renderSidebar = () => {
    if (!isDesktop) return null;

    if (isCollapsed) {
      return (
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          height='100%'
          width='80px'
          maxHeight='100vh'
          alignContent='center'
          style={{ boxSizing: 'border-box' }}
          bg='secondary/background'
          padding='s'
          m='s'
          borderRadius='m'
          position='sticky'
          left='0'
          top='0'
        >
          <MenuLight />
        </Box>
      );
    }

    return (
      <Sidebar
        icon={logoSlash}
        imageBase64={imageBase64}
        title=''
        // no-dd-sa:tsx-react/no-children-prop
        children={
          <Box display='flex' flexDirection='column' justifyContent='space-between' height='100%'>
            <Box role='navigation' ml='-xs' mr='-xs'>
              <Menu />
            </Box>
            <Box borderTop='1px solid' borderColor='grey/dark/background'>
              {!imageBase64 && (
                <Box mt='s'>
                  <Typography mb='xxxs' variant='p2/light'>
                    {defaultOrganization}
                  </Typography>
                </Box>
              )}
              {imageBase64 && (
                <Box mt='s'>
                  <Asset width='80' src={imageBase64} />
                </Box>
              )}
            </Box>
          </Box>
        }
      />
    );
  };

  const getGridTemplateColumns = () => {
    if (!isDesktop) return '1fr';
    return isCollapsed ? '100px 1fr' : 'repeat(12, 1fr)';
  };

  const getContentGridColumn = () => {
    if (!isDesktop) return undefined;
    return isCollapsed ? undefined : 'span 10';
  };

  const getSidebarGridColumn = () => {
    if (!isDesktop || isCollapsed) return undefined;
    return 'span 2';
  };

  useEffect(() => {
    if (!hasAlreadySeenTutorial(TutorialKeys.First)) {
      openDialog();
    }
  }, [hasAlreadySeenTutorial, openDialog]);

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <LayoutContext.Provider value={{ layout, setLayout, banners, setBanner }}>
      <HeadlessBanners />
      <Box height='100%' display='grid' style={{ gridTemplateColumns: getGridTemplateColumns() }}>
        {isDesktop && (
          <Box
            position='relative'
            minWidth={250}
            height={`calc(100% - ${smallSize} * 2)`}
            style={{ gridColumn: getSidebarGridColumn() }}
          >
            {renderSidebar()}
          </Box>
        )}
        {isDesktop &&
          isFFMenuLight &&
          ReactDOM.createPortal(
            <Box
              display='flex'
              position='fixed'
              left={isCollapsed ? '100px' : `calc(max(250px, 100% / 6) - ${smallSize})`}
              bg='secondary/background'
              borderRadius='50%'
              p='xxxs'
              bottom='xl'
              justifyContent='center'
              alignItems='center'
              onClick={toggleCollapsed}
              style={{ cursor: 'pointer', transform: 'translateX(-50%)', zIndex: 999 }}
              data-testid='menu-collapse-toggle'
            >
              <Svg name={isCollapsed ? 'arrowRight' : 'arrowLeft'} size='xs' />
            </Box>,
            document.body,
          )}
        <Box
          id='page-content-container'
          style={{ gridColumn: getContentGridColumn(), overflowX: 'hidden' }}
          backgroundColor='primary/background'
        >
          <div id='page-top-anchor' />
          <Header
            pageTitle={tShared(`${layout.title}.pageTitle`, {
              defaultValue: '',
            })}
            headerChildren={
              <Box position='relative' zIndex={hasBlockingError ? 10000 : undefined}>
                <UserInfo />
              </Box>
            }
          />
          <Box
            display='flex'
            flexDirection='column'
            backgroundColor='primary/background'
            role='main'
          >
            <Box
              minHeight={dynamicHeight}
              px='s'
              mb='l'
              maxWidth={genericResponsiveValue({
                breakPoints: { desktop: 'calc(100% - 100px)' },
                defaultValue: '100%',
              })}
            >
              {Object.keys(banners).length > 0 && (
                <Box
                  id='page-content-banner'
                  mb='s'
                  display='flex'
                  flexDirection='column'
                  style={{ gap: bannersGapXS }}
                >
                  {Object.entries(banners).map(([key, node]) => (
                    <Box key={key}>{node}</Box>
                  ))}
                </Box>
              )}
              {children}
              <ToastContainer aria-label='Notifiche' style={{ zIndex: 99999999 }} />
              {isFFTutorial && !userMustConfigureOrganization && (
                <ClaimsComponent roles={[Roles.Admin, Roles.HR_Delegate]}>
                  <FirstTutorial />
                </ClaimsComponent>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </LayoutContext.Provider>
  );
};

export const PageLayout: FC<Props> = ({ children }) => {
  return (
    <MenuProvider>
      <PageLayoutContent>{children}</PageLayoutContent>
    </MenuProvider>
  );
};
